/**
 * useAssetDetail — fetch a single asset's full detail (GetAsset) for the Detail
 * page. Owns detail / loading / error + a manual reload key so BOTH the header
 * (sfdcTag, maturity, …) and the Information panel (八要素 / oneLiner / arsenal)
 * read ONE shared detail and refresh together after any edit.
 *
 * Why detail (not the list AssetSummary) for the header: GetAsset is the
 * authoritative per-asset read — the list summary can lag (e.g. sfdcTag). One
 * GetAsset per selected asset feeds the whole right side.
 */
import { useEffect, useState } from 'react';
import { getIvtClient, GetAssetCommand, type AssetDetail } from '../../../client/IvtHubClient';

export interface AssetDetailData {
  detail: AssetDetail | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useAssetDetail(assetId: string | null): AssetDetailData {
  const [detail, setDetail] = useState<AssetDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!assetId) {
      setDetail(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Clear stale data from the previously-selected asset. Without this,
    // switching between two valid assetIds keeps the OLD detail on screen
    // (name / sfdcTag / motionPipeline / elements …) until the new fetch
    // resolves. Worse, the header derives its `assetId` as
    // `detail?.assetId ?? asset.assetId`, so downstream hooks like
    // useAssetAssessment inherit the stale id and don't re-fetch at all.
    setDetail(null);
    (async () => {
      try {
        const out = await getIvtClient().send(new GetAssetCommand({ assetId }));
        if (cancelled) return;
        setDetail(out.asset ?? null);
      } catch (e) {
        if (cancelled) return;
        setError((e as Error).message ?? 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assetId, reloadKey]);

  return { detail, loading, error, reload: () => setReloadKey((k) => k + 1) };
}
